/* Mezkla cart drawer — vanilla JS.
   Owns: drawer open/close, /cart.js fetch + re-render, /cart/change.js for
   quantity updates and removals (quantity 0). Other parts of the site signal
   into the drawer via custom events:
     - 'cart:open'     → open + refresh contents
     - 'cart:updated'  → refresh contents (used internally; product.js could
                         dispatch it too if you want a refresh without opening)
*/
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var configEl = drawer.querySelector('[data-cart-drawer-config]');
  var config = { collections_url: '/collections', cart_url: '/cart' };
  if (configEl) {
    try { config = JSON.parse(configEl.textContent); } catch (e) {}
  }

  var body = drawer.querySelector('[data-cart-drawer-body]');
  var footer = drawer.querySelector('[data-cart-drawer-footer]');
  var subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
  var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
  var backdrop = drawer.querySelector('[data-cart-drawer-backdrop]');

  var lastFocused = null;
  var pendingRequest = null;

  /* ─── Helpers ──────────────────────────────────────────────────────── */
  function esc(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    // EU euro format: €XX,XX (comma decimal). Mirrors snippets/price-format.liquid.
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function imageAt(url, width) {
    if (!url) return '';
    // Shopify CDN URLs accept _NNNxNNN before the file extension.
    return url.replace(/(\.[a-z]+)(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  /* ─── Render ───────────────────────────────────────────────────────── */
  function renderLine(item) {
    var variantHtml = '';
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<p class="cart-drawer__line-variant">' + esc(item.variant_title) + '</p>';
    }
    var imgHtml = '';
    if (item.image) {
      imgHtml = '<img src="' + esc(imageAt(item.image, 200)) + '" alt="' + esc(item.product_title) + '" class="cart-drawer__line-image" width="80" height="100" loading="lazy">';
    }
    return ''
      + '<li class="cart-drawer__line" data-line-key="' + esc(item.key) + '">'
      +   '<a href="' + esc(item.url) + '" class="cart-drawer__line-image-link" tabindex="-1">' + imgHtml + '</a>'
      +   '<div class="cart-drawer__line-info">'
      +     '<a href="' + esc(item.url) + '" class="cart-drawer__line-title">' + esc(item.product_title) + '</a>'
      +     variantHtml
      +     '<p class="cart-drawer__line-price">' + esc(formatMoney(item.line_price)) + '</p>'
      +     '<div class="cart-drawer__line-qty">'
      +       '<button type="button" class="cart-drawer__qty-btn" data-qty-decrement aria-label="Decrease quantity">−</button>'
      +       '<input type="number" class="cart-drawer__qty-input" data-qty-input value="' + item.quantity + '" min="0" aria-label="Quantity">'
      +       '<button type="button" class="cart-drawer__qty-btn" data-qty-increment aria-label="Increase quantity">+</button>'
      +     '</div>'
      +   '</div>'
      +   '<button type="button" class="cart-drawer__line-remove" data-line-remove aria-label="Remove ' + esc(item.product_title) + '">×</button>'
      + '</li>';
  }

  function renderEmpty() {
    return ''
      + '<div class="cart-drawer__empty">'
      +   '<p class="cart-drawer__empty-text">Your bag is empty.</p>'
      +   '<a href="' + esc(config.collections_url) + '" class="cart-drawer__continue">Browse collection</a>'
      + '</div>';
  }

  function renderCart(cart) {
    if (cart.item_count === 0) {
      body.innerHTML = renderEmpty();
      footer.setAttribute('hidden', '');
    } else {
      body.innerHTML = '<ul class="cart-drawer__lines" data-cart-drawer-lines>' +
        cart.items.map(renderLine).join('') + '</ul>';
      footer.removeAttribute('hidden');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }
    syncCountBadges(cart.item_count);
  }

  function syncCountBadges(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  /* ─── Server I/O ───────────────────────────────────────────────────── */
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    if (pendingRequest) return pendingRequest;
    setLineBusy(key, true);

    pendingRequest = fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      })
      .catch(function () {
        setLineBusy(key, false);
      })
      .finally(function () {
        pendingRequest = null;
      });

    return pendingRequest;
  }

  function setLineBusy(key, busy) {
    var line = drawer.querySelector('[data-line-key="' + CSS.escape(key) + '"]');
    if (!line) return;
    line.querySelectorAll('button, input').forEach(function (el) {
      el.disabled = busy;
    });
  }

  /* ─── Open / close ─────────────────────────────────────────────────── */
  function open() {
    lastFocused = document.activeElement;
    drawer.removeAttribute('hidden');
    // Force reflow so the [hidden]→visible state paints before we add data-open
    void drawer.offsetWidth;
    drawer.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();

    // Refresh contents on every open — handles the case where the cart was
    // mutated elsewhere (product page add, another tab, etc.).
    fetchCart().then(renderCart).catch(function () {});
  }

  function close() {
    drawer.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    // Wait for the slide-out transition to finish before fully hiding.
    window.setTimeout(function () {
      drawer.setAttribute('hidden', '');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }, 300);
  }

  /* ─── Wiring ───────────────────────────────────────────────────────── */
  // External triggers (cart icons, product page after-add)
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cart-drawer-open]');
    if (trigger) {
      e.preventDefault();
      open();
    }
  });

  document.addEventListener('cart:open', open);
  document.addEventListener('cart:updated', function () {
    fetchCart().then(renderCart).catch(function () {});
  });

  if (closeBtn) closeBtn.addEventListener('click', close);
  if (backdrop) backdrop.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
      close();
    }
  });

  // Quantity controls + remove (delegated so re-renders don't need re-binding).
  drawer.addEventListener('click', function (e) {
    var line = e.target.closest('[data-line-key]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var input = line.querySelector('[data-qty-input]');
    var current = input ? parseInt(input.value, 10) || 0 : 0;

    if (e.target.closest('[data-qty-decrement]')) {
      changeLine(key, Math.max(0, current - 1));
    } else if (e.target.closest('[data-qty-increment]')) {
      changeLine(key, current + 1);
    } else if (e.target.closest('[data-line-remove]')) {
      changeLine(key, 0);
    }
  });

  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input) return;
    var line = input.closest('[data-line-key]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    changeLine(key, value);
  });
})();
